import React, { ChangeEvent, Dispatch, useCallback, useMemo } from "react";
import { Select, Button } from "@alm/flow-ds/components";
import {
	Actions,
	removeField,
	SearchField,
	setFieldValue,
} from "./finderReducer";
import classnames from "classnames";
import DateSearchField from "./DateSearchField";
import TextSearchField from "./TextSearchField";
import SelectSearchField from "./SelectSearchField";
import LookupSearchField from "./LookupSearchField";
import { useTranslation } from "react-i18next";
import { Clause, QueryStatement } from "lib/api/types";

type SearchFieldProps = SearchField & {
	removeable?: boolean;
	index: number;
	dispatch: Dispatch<Actions>;
	searchFieldOptions: SearchField[];
	queryValue?: string;
	queryOpts?: QueryStatement | Clause;
};

function SearchFieldRow({
	removeable,
	index,
	dispatch,
	searchFieldOptions,
	queryValue,
	queryOpts,
	...elementProps
}: SearchFieldProps) {
	const { t } = useTranslation();
	const remove = useCallback(
		() => dispatch(removeField(index)),
		[dispatch, index]
	);

	const onChange = useCallback(
		(e: ChangeEvent<HTMLSelectElement>) => {
			dispatch(setFieldValue(e.currentTarget.value, index));
		},
		[index, dispatch]
	);

	const searchInput = useMemo(() => {
		if (!elementProps.name || !elementProps.label || !dispatch) return null;
		const props = {
			index,
			dispatch,
			queryValue,
			queryOpts,
			...elementProps,
		};
		switch (props.elem) {
			case "date":
				return <DateSearchField {...props} />;
			case "text":
				return <TextSearchField {...props} />;
			case "select":
				return <SelectSearchField {...props} />;
			case "lookup":
				return <LookupSearchField {...props} />;
			default:
				return null;
		}
	}, [dispatch, elementProps, index, queryOpts, queryValue]);

	const { label, name } = elementProps;

	return (
		<div className="form-row pt-4 border-bottom pb-3">
			<div className="col-6 col-md-3">
				<Select
					onChange={onChange}
					labelHidden={true}
					label={t("elements.finder.add-search-field")}
					value={name}>
					{name ? (
						<option value={name}>{label}</option>
					) : (
						<option aria-hidden defaultValue={undefined}>
							{t("elements.finder.add-search-field")}
						</option>
					)}

					{searchFieldOptions.map(op => (
						<option value={op.name} key={op.name}>
							{op.label}
						</option>
					))}
				</Select>
			</div>
			{name && (
				<>
					{searchInput}

					<div className="col-2 col-md-2 col-lg-1">
						<Button
							aria-hidden={name === undefined ? true : false}
							className={classnames(
								"btn-sm btn-default mt-3 mt-md-1",
								name === undefined ? "invisible" : null
							)}
							disabled={!removeable}
							onClick={remove}
							label={t("elements.finder.removeFieldBtnLabel")}
						/>
					</div>
				</>
			)}
		</div>
	);
}

export default SearchFieldRow;
